import React, { useState } from 'react';
import TimerDisplay from './TimerDisplay';
import { Clock, Crown, Handshake, Trophy, Save } from 'lucide-react';
import useGameStore from '../store/gameStore';

type GameOverProps = {
  modelName: string;
  modelTimeRemaining: number;
  playerTimeRemaining: number;
  timeOut: 'player' | 'model' | null;
  isCheckmate: boolean;
  isDraw: boolean;
  boardOrientation: 'white' | 'black';
  game: any;
  onNewGame: () => void;
  isMobile?: boolean;
};

const getGameOverMessage = (
  timeOut: 'player' | 'model' | null, 
  isCheckmate: boolean, 
  isDraw: boolean,
  boardOrientation: 'white' | 'black',
  game: any
) => {
  if (timeOut) {
    return {
      icon: <Clock size={24} />,
      message: timeOut === 'player' ? 'You Lose!' : 'You Win!',
      color: timeOut === 'player' ? 'text-red-500' : 'text-green-500'
    };
  }
  
  if (isCheckmate) {
    const winnerIsWhite = game.turn() === 'b';
    const playerIsWhite = boardOrientation === 'white';
    const playerWon = (winnerIsWhite && playerIsWhite) || (!winnerIsWhite && !playerIsWhite);
    return {
      icon: <Crown size={24} />,
      message: playerWon ? 'Checkmate!' : 'Checkmate!',
      color: playerWon ? 'text-green-500' : 'text-red-500'
    };
  }
  
  if (isDraw) {
    return {
      icon: <Handshake size={24} />,
      message: 'Draw!',
      color: 'text-yellow-500'
    };
  }
  
  return {
    icon: null,
    message: 'Game Over!',
    color: 'text-terminal-green'
  };
};

const didPlayerWin = (
  timeOut: 'player' | 'model' | null,
  isCheckmate: boolean,
  boardOrientation: 'white' | 'black',
  game: any
) => {
  if (timeOut === 'model') return true;
  if (timeOut === 'player') return false;
  
  if (isCheckmate) {
    const winnerIsWhite = game.turn() === 'b';
    const playerIsWhite = boardOrientation === 'white';
    return (winnerIsWhite && playerIsWhite) || (!winnerIsWhite && !playerIsWhite);
  }
  
  return false;
};

const GameOver: React.FC<GameOverProps> = ({
  modelName,
  modelTimeRemaining,
  playerTimeRemaining,
  timeOut,
  isCheckmate,
  isDraw,
  boardOrientation,
  game,
  onNewGame,
  isMobile = false
}) => {
  const [playerNameInput, setPlayerNameInput] = useState('');
  const { 
    currentScore, 
    showHighScoreEntry, 
    addHighScore 
  } = useGameStore();
  
  const { icon, message, color } = getGameOverMessage(
    timeOut,
    isCheckmate,
    isDraw,
    boardOrientation,
    game
  );
  
  const playerWon = didPlayerWin(
    timeOut,
    isCheckmate,
    boardOrientation,
    game
  );

  const handleSubmitHighScore = (e: React.FormEvent) => {
    e.preventDefault();
    if (playerNameInput.trim()) {
      addHighScore(playerNameInput.trim());
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      {/* Timers section - compact on mobile */}
      <div className={`${isMobile ? 'flex justify-between items-center' : ''}`}>
        <TimerDisplay 
          label={modelName} 
          timeRemaining={modelTimeRemaining} 
          isActive={false} 
        />
        
        {isMobile ? (
          <span className="mx-2 text-terminal-green">|</span>
        ) : (
          <div className="border-t rounded border-terminal-green/30 my-1 sm:my-2"></div>
        )}
        
        <TimerDisplay 
          label="Player 1" 
          timeRemaining={playerTimeRemaining} 
          isActive={false}
        />
      </div>
      
      {/* Game result message */}
      <div className="font-press-start text-base p-4 border border-dashed rounded border-terminal-green blink flex flex-col items-center bg-black/90">
        <div className="flex items-center gap-2 justify-center">
          <span className={color}>{icon}</span>
          <span className={`text-center ${color} break-words max-w-full`}>{message}</span>
        </div>
      </div>
      
      {/* Score display - only show when player wins */}
      {playerWon && (
        <div className="mt-2 sm:mt-4 p-3 border rounded border-terminal-green flex flex-col items-center gap-2 bg-black/80">
          <div className="flex items-center gap-2 justify-center">
            <Trophy size={20} className="text-yellow-500" />
            <span className="font-vt323 text-xl">Score: {currentScore}</span>
          </div>
          
          {showHighScoreEntry && (
  <div className="w-full mt-2">
    <p className="text-center text-yellow-500 mb-2 font-vt323">New High Score!</p>
    <form onSubmit={handleSubmitHighScore} className="flex gap-2" autoComplete="off">
      <div
        contentEditable
        onInput={(e) => {
          const text = (e.currentTarget.textContent || '').slice(0, 14); // limita a 14 caratteri
          const sanitized = text.replace(/[^A-Za-z0-9_-]/g, '').toUpperCase();
          setPlayerNameInput(sanitized);

          // Aggiorna il testo direttamente (sanitizza anche visivamente)
          if (e.currentTarget.textContent !== sanitized) {
            e.currentTarget.textContent = sanitized;
            // Move cursor to end
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(e.currentTarget);
            range.collapse(false);
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        }}
        data-placeholder="ENTER YOUR NAME"
        className="w-[75%] bg-bg-dark text-terminal-green border rounded border-terminal-green p-2 font-vt323 text-xl uppercase focus:outline-none"
        style={{ minHeight: '48px' }} // altezza fissa stile input
      />
      <button 
        type="submit"
        className="w-[25%] rounded bg-terminal-green text-black p-2 font-vt323 flex items-center justify-center"
        aria-label="Save"
      >
        <Save size={18} />
      </button>
    </form>
  </div>
)}
        </div>
      )}
      
      <button
        className="w-full mt-2 bg-bg-dark text-terminal-green border rounded border-terminal-green p-3 font-vt323 text-xl cursor-pointer hover:bg-dark-green"
        onClick={onNewGame}
      >
        $ CLOSE
      </button>
    </div>
  );
};

export default GameOver;